import React from 'react';
import { CalendarDaysIcon, MapPinIcon } from 'lucide-react';
import { FloatingPhoto } from '../motion/FloatingPhoto';
import { Reveal } from '../motion/Reveal';
import { Container, Eyebrow, Section } from '../ui/Section';
import { ButtonExternal } from '../ui/Button';
import { PhotoFrame } from '../ui/PhotoFrame';
import { DoodleArrow, DoodleSquiggle, DoodleStar, DoodleUnderline } from '../brand/Doodles';
import { externalLinks, meeting } from '../../data/site';
import { heroPhotos } from '../../data/photos';
export function Hero() {
  return <Section className="overflow-hidden bg-[#F4F7FB] pb-16 pt-12 sm:pb-20 sm:pt-16" labelledBy="hero-heading" data-id="element-163" data-mp="5o5m328wliu8">
      <Container className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-14" data-id="element-164" data-mp="3rwos3vhz2hd5">
        <Reveal data-id="element-165" data-mp="3rz1jez9me42v">
          <Eyebrow data-id="element-166" data-mp="1v451h13484ib">Baptist Collegiate Ministries at UCF</Eyebrow>

          <h1 id="hero-heading" className="mt-4 max-w-[15ch] font-display text-[2.05rem] font-extrabold leading-[1.08] tracking-tight text-brand-navy sm:text-[2.9rem] lg:text-[3.5rem]" data-id="element-167" data-mp="1v2aruixanytj">
            Christian community for your life{' '}
            <span className="relative inline-block whitespace-nowrap" data-id="element-168" data-mp="1rw71gbl9g2jq">
              at UCF.
              <DoodleUnderline className="absolute -bottom-2 left-0 h-3 w-full sm:-bottom-3 sm:h-4" data-id="element-169" data-mp="1czgc6v5aw77u" />
            </span>
          </h1>

          <p className="mt-7 max-w-[54ch] font-sans text-[17px] leading-relaxed text-ink sm:text-lg" data-id="element-170" data-mp="1hzc6u3s3ymaq">
            BCM brings students together for worship, honest conversations, shared meals, service,
            and friendships that make a big campus feel smaller. Come meet people, ask questions,
            and grow in your relationship with Jesus.
          </p>

          <div className="relative mt-9 w-fit" data-id="element-171" data-mp="20usr0qp5ghu0">
            <DoodleStar className="pointer-events-none absolute -left-7 -top-5 h-7 w-7 sm:-left-9 sm:h-8 sm:w-8" color="var(--bcm-yellow)" data-id="element-172" data-mp="2olohnkhvd6zo" />
            <DoodleSquiggle className="pointer-events-none absolute -right-10 -top-4 hidden h-6 w-20 sm:block" color="var(--bcm-light-blue)" data-id="element-173" data-mp="2tehihxa7vv6h" />
            <ButtonExternal href={externalLinks.getConnectedForm} className="min-h-[56px] px-8 py-4 text-[17px] sm:text-lg" data-id="element-174" data-mp="2ya3hozjd5xen">
              Get Connected
            </ButtonExternal>
            <DoodleUnderline className="pointer-events-none absolute -bottom-3 left-4 h-2.5 w-[70%]" color="var(--bcm-yellow)" data-id="element-175" data-mp="2payidqzza01f" />
          </div>

          <div className="mt-12 max-w-lg rounded-[20px] border-2 border-brand-navy bg-white p-5" data-id="element-176" data-mp="2fgptdda5op80">
            <p className="flex items-start gap-2.5 font-meta text-sm font-semibold text-brand-navy" data-id="element-177" data-mp="2ahn0ssbd4bgx">
              <CalendarDaysIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" data-id="element-178" data-mp="26g1hao4boyxr" />
              {meeting.summary}
            </p>
            <p className="mt-2.5 flex items-start gap-2.5 font-meta text-sm text-ink" data-id="element-179" data-mp="1wjewfzbzy7vq">
              <MapPinIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-180" data-mp="c2617bsm8yt6" />
              {meeting.location}
            </p>
            <p className="mt-3 font-meta text-[12.5px] leading-relaxed text-ink" data-id="element-181" data-mp="gzpi6jgp7pot">
              {meeting.clarification}
            </p>
          </div>
        </Reveal>

        <Reveal x={18} y={16} delay={0.1} className="relative" data-id="element-182" data-mp="q26yyidl3q4f">
          <DoodleStar className="absolute -left-3 -top-4 z-10 h-9 w-9 sm:-left-6 sm:h-12 sm:w-12" data-id="element-183" data-mp="pzcg2va7ydd4" />
          <DoodleArrow className="absolute -bottom-6 -left-2 z-10 hidden h-16 w-16 lg:block" color="var(--bcm-pink)" data-id="element-184" data-mp="3pezdn8q31wnm" />
          <DoodleSquiggle className="absolute -right-2 -top-7 z-10 h-8 w-28 sm:-right-6 sm:h-10 sm:w-36" data-id="element-185" data-mp="2b1ka6jdr3vp" />

          <div className="grid grid-cols-5 gap-3 sm:gap-4" data-id="element-186" data-mp="73mayz6zp0v0">
            <FloatingPhoto className="col-span-5 sm:col-span-3" range={7} duration={7.5} data-id="element-187" data-mp="36que6dggkxum">
              <PhotoFrame photo={heroPhotos[0]} ratio="aspect-[4/5]" rotate="-rotate-1" className="h-full transition-transform duration-300 hover:scale-[1.015]" data-id="element-188" data-mp="3aro1b4m2u8mg" />
            </FloatingPhoto>
            <div className="col-span-5 flex flex-row gap-3 sm:col-span-2 sm:flex-col sm:gap-4" data-id="element-189" data-mp="2mm3l3zgy29bn">
              <FloatingPhoto className="min-w-0 flex-1" range={5} duration={6} delay={0.8} data-id="element-190" data-mp="31efaz74279hd">
                <PhotoFrame photo={heroPhotos[1]} ratio="aspect-square" rotate="rotate-2" className="transition-transform duration-300 hover:scale-[1.015]" data-id="element-191" data-mp="2wl2uxldg4b08" />
              </FloatingPhoto>
              <FloatingPhoto className="min-w-0 flex-1" range={4} duration={8.5} delay={0.3} data-id="element-192" data-mp="3kax49syk3hfq">
                <PhotoFrame photo={heroPhotos[2]} ratio="aspect-square" rotate="-rotate-2" className="transition-transform duration-300 hover:scale-[1.015]" data-id="element-193" data-mp="3fdx39wgkd4vh" />
              </FloatingPhoto>
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>;
}